import { getBreakStyle, hasContent, isFlexRow, isGrid, removeEmptyParents } from './dom'
import renderFlexingItem from './render_flexing_item'
import { isPdf, renderPdf } from './render_pdf'
import { createPage, isOverflowing, isPageEmpty, moveBreakAfterAvoids, pageContentWidth, renderElement } from './page'
import breakPage from './break_page'
import splitWideTables from './split_wide_tables'

export default async function pager() {
  splitWideItemTables()
  addRefs()
  await chunk()
  normalizeTableHeadersAcrossPages()
  removeEmptyDuplicateFragments()
  removeEmptyPages()
}

// Splits ngie author tables that are wider than a printable page into stacked
// tables before refs are assigned, so the cloned tables receive their own refs
// and are paged like any other content.
function splitWideItemTables() {
  const measuringPage = createPage()
  splitWideTables(document.querySelector('#un-formatted'), pageContentWidth(measuringPage))
  measuringPage.remove()
}

function addRefs() {
  const elements = document.querySelector('#un-formatted').querySelectorAll('*')
  elements.forEach((el) => addRef(el))
}

function addRef(element) {
  element.setAttribute('data-ref', getDataRef())
}

function chunk() {
  return renderChildren(document.querySelector('#un-formatted'), [], [createPage()])
}

async function renderChildren(element, parentChain = [], pages) {
  const newParentChain = [...parentChain]
  if (element.id !== 'un-formatted') {
    newParentChain.push(element)
  }
  for (const child of Array.from(element.children)) {
    if (getBreakStyle(child).before === 'always') {
      pages.push(createPage())
    }
    if (isPdf(child)) {
      await renderPdf(child, newParentChain, pages)
    } else if (isGrid(child)) {
      await renderGrid(child, newParentChain, pages)
    } else if (isFlexRow(child)) {
      await renderFlexingItem(child, newParentChain, pages)
    } else if (
      !hasContent(child) &&
      !element.classList.contains('math-tex') &&
      !element.classList.contains('MathJax')
    ) {
      await renderChildren(child, newParentChain, pages)
    } else {
      let page = pages[pages.length - 1]
      let lastRenderedElement = await renderElement(child, newParentChain, page)
      let tries = 0
      while (isOverflowing(page) && !lastRenderedElement.getAttribute('data-unbreakable') && tries < 100) {
        const { newChunk, newPage } = breakPage(lastRenderedElement, newParentChain, page)
        lastRenderedElement = newChunk
        pages.push(newPage)
        page = newPage
        tries++
      }
    }
  }
}

async function renderGrid(gridItem, parentChain, pages) {
  const page = pages[pages.length - 1]
  const renderedGrid = await renderElement(gridItem, parentChain, page)
  if (isOverflowing(page)) {
    const oldParent = renderedGrid.parentElement
    const { newPage } = breakPage(renderedGrid, parentChain, page)
    removeEmptyParents(oldParent)
    moveBreakAfterAvoids(page, newPage)
    pages.push(newPage)
  }
}

// Two problems arise once the pager chunks tables across pages:
//
//   1. A continuation keeps the original table's `data-ref` but loses its
//      `<thead>` (the header stays on the first page), so the column headings
//      vanish on every following page.
//   2. The pager rebuilds broken content by reverse-iterating children and
//      `prepend`-ing them, which can leave a table's `<thead>` positioned after
//      its `<tbody>`. Downstream passes and the repeated-header logic below
//      assume the header leads the table, so a stray out-of-order `<thead>`
//      must be moved back to the front.
//
// This pass fixes both: it re-adds a copy of the header to continuations that
// lost it, and moves any out-of-order `<thead>` back to the front of its table.
// While chunking, the pager reserves space (padding-top) for this yet-to-be-
// added header so it doesn't overflow the page; that reservation is dropped
// here once the real header is in place.
export function normalizeTableHeadersAcrossPages() {
  const pages = Array.from(document.querySelectorAll('.page'))

  // Remember the first real header a given table (by data-ref) carries across
  // pages. Empty `<thead>` shells are ignored: the bottom-up break can leave a
  // headerless continuation with a blank `<thead>` while the real header rides
  // the rows onto a later chunk, so the first non-empty header — wherever it
  // landed — is the one to repeat.
  const headerByRef = new Map()
  pages.forEach((page) => {
    page.querySelectorAll('table[data-ref]').forEach((table) => {
      const ref = table.getAttribute('data-ref')
      if (headerByRef.has(ref)) {
        return
      }
      const thead = table.querySelector(':scope > thead')
      if (thead && headerHasContent(thead)) {
        headerByRef.set(ref, thead)
      }
    })
  })

  pages.forEach((page) => {
    page.querySelectorAll('table[data-ref]').forEach((table) => {
      // The padding-top reservation (reserveRepeatedHeaderSpace) only had to hold
      // space while the pager was chunking rows. Now that headers are being
      // placed it has served its purpose, so drop it up front on every table —
      // including the branches below that add no header (e.g. a continuation
      // whose ref never carried a real header) — so no path can leave a stray
      // gap at the top of a continuation.
      clearReservedHeaderSpace(table)

      // Drop any empty `<thead>` shell first, so it isn't mistaken for a real
      // header (which would suppress the repeated copy this table needs).
      const existing = table.querySelector(':scope > thead')
      if (existing && !headerHasContent(existing)) {
        existing.remove()
      }

      const ownHeader = table.querySelector(':scope > thead')
      if (ownHeader) {
        // Keep an existing header, but make sure it leads the table.
        if (ownHeader !== table.firstElementChild) {
          table.prepend(ownHeader)
        }
        return
      }

      // No header of its own: if this table (by ref) had one on another page
      // and there are body rows to head, insert a copy.
      const header = headerByRef.get(table.getAttribute('data-ref'))
      if (!header || !tableHasBodyRows(table)) {
        return
      }
      const repeated = header.cloneNode(true)
      repeated.classList.add('print-repeated-header')
      // Strip data-refs so the cloned header doesn't duplicate existing refs.
      repeated.removeAttribute('data-ref')
      repeated.querySelectorAll('[data-ref]').forEach((node) => node.removeAttribute('data-ref'))
      table.prepend(repeated)
    })
  })
}

// A `<thead>` counts as a real header only when it carries text or media; a
// blank shell left behind by the row-breaker does not.
//
// The media check deliberately omits `tr` (unlike elementHasContent below): an
// empty shell is itself a `<tr>` of blank `<th>`, so counting `tr` as content
// would make every empty shell look "real" and suppress the repeated-header
// copy the continuation needs. Do not add `tr` here.
function headerHasContent(thead) {
  return (thead.textContent || '').trim().length > 0 || thead.querySelector('img, svg, canvas') !== null
}

// Removes the header-height reservation the pager left on a continuation once
// the real header has been placed (see reserveRepeatedHeaderSpace).
function clearReservedHeaderSpace(table) {
  if (table.dataset.repeatedHeaderReserve === undefined) {
    return
  }
  table.style.removeProperty('padding-top')
  delete table.dataset.repeatedHeaderReserve
}

// When an item is split across pages, the pager rebuilds its wrapper chain on
// each page. If all of an item's real content moves to a later page, the
// earlier page can be left with an empty clone of those wrappers — e.g. a
// bordered cloze box holding only a blank `<p> </p>` — which prints as a stray
// empty box. A related case: the wide-table splitter can strand a table's real
// `<thead>` on its own chunk with no rows (its body flowed to the neighboring
// page and picked up a repeated header there), which prints as an orphan header
// box. Both are duplicates: another element with the same data-ref carries the
// real body content elsewhere. Remove those body-less copies, leaving
// side-by-side columns (kept intentionally empty for layout) alone.
export function removeEmptyDuplicateFragments() {
  const all = Array.from(document.querySelectorAll('#pages-container [data-ref]'))

  const refsWithContent = new Set()
  all.forEach((el) => {
    if (hasBodyContent(el)) {
      refsWithContent.add(el.getAttribute('data-ref'))
    }
  })

  // Document order visits ancestors before descendants, so removing the
  // outermost empty copy first detaches its (also empty) children; the
  // isConnected guard skips those on later iterations.
  all.forEach((el) => {
    if (!el.isConnected || isSideBySideColumn(el)) {
      return
    }
    if (refsWithContent.has(el.getAttribute('data-ref')) && !hasBodyContent(el)) {
      const parent = el.parentElement
      el.remove()
      // Drop the now-empty wrappers left above the removed fragment, but never a
      // side-by-side column (kept empty on purpose to hold the two-column layout).
      removeEmptyParents(parent, isSideBySideColumn)
    }
  })
}

// A table has real content only when it carries actual body rows; a lone
// `<thead>` does not count. Shared by both post-chunk passes so they agree on
// what an orphan (header-only) table is — see hasBodyContent and
// normalizeTableHeadersAcrossPages.
function tableHasBodyRows(table) {
  return table.querySelector(':scope > tbody > tr, :scope > tr') !== null
}

// Whether a fragment carries real body content. For a table that means actual
// rows: a header-only table is an orphan header stranded by the wide-table
// splitter and must be treated as body-less even though its header `<tr>`/text
// would satisfy elementHasContent. Everything else defers to elementHasContent
// (text or embedded media).
function hasBodyContent(element) {
  if (element.tagName === 'TABLE') {
    return tableHasBodyRows(element)
  }
  return elementHasContent(element)
}

// Deep content check for a single fragment (its whole subtree). Distinct from
// dom.js's shallow `hasContent`, which only inspects an element's own direct text
// nodes and tag name — this pass needs the subtree-wide variant.
function elementHasContent(element) {
  return (element.textContent || '').trim().length > 0 || element.querySelector('img, svg, canvas, tr') !== null
}

// Side-by-side columns are kept even when empty so the two-column layout holds;
// adjustBorders (render_flexing_item) drops their border instead of removing them.
function isSideBySideColumn(element) {
  return element.classList.contains('border-solid')
}

let PRINT_ID = 1
function getDataRef() {
  return `ele-${PRINT_ID++}`
}

function removeEmptyPages() {
  const pages = document.querySelectorAll('.page')
  for (let i = 0; i < pages.length; ++i) {
    const page = pages[i]
    if (isPageEmpty(page)) {
      page.remove()
    }
  }
}
